/**
 * Equipment Return System - Unified Types
 * ระบบการส่งคืนอุปกรณ์แบบรวมศูนย์
 */

// ============================================
// Enums - ประเภทและสถานะต่างๆ
// ============================================

/**
 * ประเภทต้นทางของการส่งคืน
 * - REPLACEMENT: จากการเปลี่ยนอุปกรณ์
 * - CANCELLATION: จากการยกเลิกสัญญา
 * - CONTRACT_END: จากสัญญาหมดอายุ
 * - NORMAL_RETURN: การส่งคืนปกติ
 */
export type ReturnSourceType = 'REPLACEMENT' | 'CANCELLATION' | 'CONTRACT_END' | 'NORMAL_RETURN';

/**
 * สถานะของรายการส่งคืน
 * - PENDING: รอดำเนินการ
 * - SCHEDULED: กำหนดวันนัดแล้ว
 * - IN_PROGRESS: กำลังดำเนินการ
 * - COMPLETED: ส่งคืนเรียบร้อย
 * - CANCELLED: ยกเลิก
 * - LOST: สูญหาย
 * - AT_COMPANY_OFFICE: อยู่ที่บริษัท รปภ (FB-2026-0010 · ยังเปิด — ของยังไม่กลับคลัง)
 */
export type ReturnItemStatus = 'PENDING' | 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'LOST' | 'AT_COMPANY_OFFICE';

/**
 * สภาพอุปกรณ์ที่ส่งคืน
 * - GOOD: สภาพดี
 * - DAMAGED: ชำรุด
 * - LOST: สูญหาย
 */
export type EquipmentCondition = 'GOOD' | 'DAMAGED' | 'LOST';

/**
 * วิธีการติดต่อ
 * - PHONE: โทรศัพท์
 * - LINE: ไลน์
 * - VISIT: เยี่ยมสถานที่
 * - OTHER: อื่นๆ
 */
export type ContactMethod = 'PHONE' | 'LINE' | 'VISIT' | 'OTHER';

/**
 * ผลการติดต่อ
 * - CONTACTED: ติดต่อสำเร็จ
 * - NO_ANSWER: ไม่รับสาย
 * - WRONG_NUMBER: หมายเลขผิด
 * - REFUSED: ปฏิเสธการติดต่อ
 */
// SYSTEM = แถวที่ 'ระบบ' เขียนให้ (เช่น เปิดรายการที่ปิดผิดกลับมาใหม่) ไม่ใช่ผลการติดต่อลูกค้า
// — ผู้ใช้เลือกเองไม่ได้ backend ปฏิเสธที่ชั้น schema (422)
export type ContactResult = 'CONTACTED' | 'NO_ANSWER' | 'WRONG_NUMBER' | 'REFUSED' | 'SYSTEM';

// ============================================
// Main Interfaces - โครงสร้างข้อมูลหลัก
// ============================================

/**
 * คำขอส่งคืนอุปกรณ์
 * รวบรวมรายการอุปกรณ์ที่ต้องส่งคืนในแต่ละครั้ง
 */
export interface ReturnRequest {
  id: number;
  rental_id: number;
  source_type: ReturnSourceType;
  device_replacement_id?: number;
  cancellation_notice_id?: number;
  request_date: string;
  notes?: string;
  created_by_user_id: number;
  created_at: string;

  // Nested relationships (จะมีข้อมูลเมื่อ expand=true)
  rental?: {
    rental_number: string;
    company?: { name: string };
    site?: { name: string };
  };
  items?: ReturnItem[];
}

/**
 * รายการอุปกรณ์ที่ต้องส่งคืน — **1 แถว = ของ 1 ชิ้น**
 *
 * กติกา (เปลี่ยนรอบนี้ · feedback ทิวากร "มันก็ไม่ได้แยกให้เห็นชัด ๆ ว่าซิมหายอย่างเดียว"):
 * - แถวของเครื่อง → `device_id` มีค่า, `sim_id` = null
 * - แถวของซิม    → `sim_id` มีค่า, `device_id` = null
 * - รายการเช่าที่มีทั้งเครื่องและซิม จะได้ใบตามเก็บ **2 แถว** ผูกกันด้วย `rental_item_id` เดียวกัน
 *   → ปิดแยกกันได้ · `equipment_condition` ต่างกันได้ · `actual_return_date` ต่างกันได้
 *
 * ⚠️ ข้อมูลเก่าที่ปิดไปแล้วก่อนเปลี่ยนกติกา ยังมีทั้ง `device_id` และ `sim_id` ในแถวเดียว
 *    ทุกจอต้องแสดงแถวแบบนั้นได้โดยไม่พัง — ใช้ `utils/returnItemLabel.describeReturnItem()`
 *    ซึ่งจะคืน kind = 'COMBINED' + ป้าย "รายการรวมแบบเก่า"
 *
 * 🚫 ห้ามเขียนสูตรป้ายชื่อของชิ้นเองในแต่ละจอ (เช่น `device?.code || sim?.sim_number`)
 *    เพราะสูตรแบบนั้นทำให้ซิมกลายเป็น "ตัวสำรอง" ของเครื่อง ซึ่งเป็นต้นเหตุของ feedback ข้างต้น
 */
export interface ReturnItem {
  id: number;
  return_request_id: number;
  /** มีค่าเมื่อแถวนี้คือ "เครื่อง" — แถวซิมต้องเป็น null */
  device_id?: number;
  /** มีค่าเมื่อแถวนี้คือ "ซิม" — แถวเครื่องต้องเป็น null */
  sim_id?: number;
  /** กุญแจจับกลุ่ม: แถวเครื่องกับแถวซิมของรายการเช่าเดียวกันใช้ค่านี้เท่ากัน */
  rental_item_id?: number;
  status: ReturnItemStatus;
  expected_return_date?: string;
  actual_return_date?: string;
  pickup_location?: string;
  contact_person?: string;
  contact_phone?: string;
  /** เบอร์/ชื่อผู้ติดต่อที่ backend resolve ให้สำหรับโทรตามเก็บ — ลำดับ: กรอกมือ → เคสซ่อม(รปภหน้างาน) → รปภ/ผู้ติดต่อโครงการ */
  resolved_contact_name?: string | null;
  resolved_contact_phone?: string | null;
  resolved_contact_source?: 'manual' | 'case' | 'site' | null;
  equipment_condition?: EquipmentCondition;
  condition_notes?: string;
  created_at: string;

  // Nested relationships (จะมีข้อมูลเมื่อ expand=true)
  // ⚠️ ไม่ expand ไม่ได้แปลว่าแถวนี้ไม่มีของ — ต้องดู device_id/sim_id ประกอบเสมอ
  device?: {
    id: number;
    serial_number?: string;
    device_code?: string;
    /** บาง endpoint คืนชื่อฟิลด์เป็น `code` */
    code?: string;
    model_name?: string;
  };
  sim?: {
    id: number;
    sim_number?: string;
    phone_number?: string;
    carrier?: string;
  };
  return_request?: ReturnRequest;

  // Computed fields (จาก backend)
  follow_up_count?: number;
  last_follow_up_date?: string;
}

/**
 * การติดตามผลการส่งคืน
 * บันทึกการติดต่อและการดำเนินการในแต่ละครั้ง
 */
export interface ItemFollowUp {
  id: number;
  return_item_id: number;
  follow_up_date: string;
  contact_method: ContactMethod;
  contact_result: ContactResult;
  action_taken: string;
  next_action?: string;
  next_follow_up_date?: string;
  notes?: string;
  created_by_user_id: number;
  follow_up_by_username?: string;
  follow_up_by_fullname?: string;
  created_at: string;

  // Nested relationships
  created_by?: {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
  };
}

// ============================================
// API Request/Response Types
// ============================================

/**
 * พารามิเตอร์สำหรับค้นหารายการส่งคืน
 */
export interface ReturnItemListParams {
  status?: ReturnItemStatus;
  source_type?: ReturnSourceType;
  rental_id?: number;
  device_id?: number;
  sim_id?: number;
  /** โครงการของสัญญาต้นทาง (28 ต.ค. 2026 — การ์ดในหน้ารายละเอียดโครงการ) */
  site_id?: number;
  overdue_only?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

/**
 * ข้อมูลสำหรับอัพเดทรายการส่งคืน
 */
export interface ReturnItemUpdateData {
  status?: ReturnItemStatus;
  expected_return_date?: string;
  actual_return_date?: string;
  pickup_location?: string;
  contact_person?: string;
  contact_phone?: string;
  equipment_condition?: EquipmentCondition;
  condition_notes?: string;
}

/**
 * ข้อมูลสำหรับสร้างการติดตามผล
 */
export interface ItemFollowUpCreateData {
  follow_up_date: string;
  contact_method: ContactMethod;
  contact_result: ContactResult;
  action_taken: string;
  next_action?: string;
  next_follow_up_date?: string;
}

/**
 * ข้อมูลสำหรับติดตามผลหลายรายการพร้อมกัน
 */
export interface BulkFollowUpData {
  item_ids: number[];
  follow_up_data: ItemFollowUpCreateData;
}

/**
 * สถิติสำหรับ Dashboard
 */
export interface DashboardStats {
  total_items: number;
  pending_count: number;
  scheduled_count: number;
  in_progress_count: number;
  completed_count: number;
  cancelled_count: number;
  lost_count: number;
  overdue_count: number;
  by_source_type: {
    replacement: number;
    cancellation: number;
    contract_end: number;
    normal_return: number;
  };
}

/**
 * ข้อมูลสำหรับสร้างคำขอส่งคืนแบบ Manual
 */
export interface ManualReturnRequestData {
  source_type: 'MANUAL';
  rental_id: number;
  request_date: string;
  notes?: string;
  items: {
    device_id?: number;
    sim_id?: number;
    rental_item_id?: number;
    expected_return_date?: string;
    pickup_location?: string;
    contact_person?: string;
    contact_phone?: string;
  }[];
}

// ============================================
// UI Helper Types
// ============================================

/**
 * ข้อมูลแบบย่อสำหรับ Dropdown/Select
 */
export interface RentalOptionForReturn {
  id: number;
  rental_number: string;
  company_name: string;
  site_name: string;
}

/**
 * Badge Color Mapping สำหรับ Status
 */
export const RETURN_STATUS_COLORS: Record<ReturnItemStatus, string> = {
  PENDING: 'yellow',
  SCHEDULED: 'blue',
  IN_PROGRESS: 'purple',
  COMPLETED: 'green',
  CANCELLED: 'gray',
  LOST: 'red',
  AT_COMPANY_OFFICE: 'teal',
};

/**
 * Badge Color Mapping สำหรับ Source Type
 */
export const RETURN_SOURCE_COLORS: Record<ReturnSourceType, string> = {
  REPLACEMENT: 'orange',
  CANCELLATION: 'red',
  CONTRACT_END: 'purple',
  NORMAL_RETURN: 'blue',
};

/**
 * ข้อความแสดงผลสำหรับ Status (ภาษาไทย)
 */
export const RETURN_STATUS_LABELS: Record<ReturnItemStatus, string> = {
  PENDING: 'รอดำเนินการ',
  SCHEDULED: 'กำหนดวันนัดแล้ว',
  IN_PROGRESS: 'กำลังดำเนินการ',
  COMPLETED: 'ส่งคืนเรียบร้อย',
  CANCELLED: 'ยกเลิก',
  LOST: 'สูญหาย',
  AT_COMPANY_OFFICE: 'อยู่ที่บริษัท รปภ',
};

/**
 * ข้อความแสดงผลสำหรับ Source Type (ภาษาไทย)
 */
export const RETURN_SOURCE_LABELS: Record<ReturnSourceType, string> = {
  REPLACEMENT: 'เปลี่ยนอุปกรณ์',
  CANCELLATION: 'ยกเลิกสัญญา',
  CONTRACT_END: 'สัญญาหมดอายุ',
  NORMAL_RETURN: 'ส่งคืนปกติ',
};

/**
 * ข้อความแสดงผลสำหรับ Contact Result (ภาษาไทย)
 */
export const CONTACT_RESULT_LABELS: Record<ContactResult, string> = {
  CONTACTED: 'ติดต่อสำเร็จ',
  NO_ANSWER: 'ไม่รับสาย',
  WRONG_NUMBER: 'หมายเลขผิด',
  REFUSED: 'ปฏิเสธการติดต่อ',
  SYSTEM: 'ระบบบันทึกให้',
};
